import React from 'react';

export type ReturnBillingRolloutMode = 'OFF' | 'SHADOW' | 'LIVE';

export type ReturnBillingRollout = {
  storeId?: string | null;
  pricingVersion?: number | string | null;
  rolloutMode?: ReturnBillingRolloutMode | string | null;
  effectiveAt?: string | null;
  blockedReason?: string | null;
};

type ReturnBillingAggregateSum = {
  amount?: number | string | null;
  carrierCost?: number | string | null;
  serviceFee?: number | string | null;
};

type ReturnBillingAggregateBucket = {
  aggregate?: {
    count?: number | string | null;
    sum?: ReturnBillingAggregateSum | null;
  } | null;
};

export type ReturnBillingSummaryQueryResult = {
  liveLabels?: ReturnBillingAggregateBucket | null;
  liveKeepItems?: ReturnBillingAggregateBucket | null;
  shadowLabels?: ReturnBillingAggregateBucket | null;
  shadowKeepItems?: ReturnBillingAggregateBucket | null;
  pending?: ReturnBillingAggregateBucket | null;
  failed?: ReturnBillingAggregateBucket | null;
  invalidCurrency?: ReturnBillingAggregateBucket | null;
};

export type ReturnBillingPricingStatusQueryResult = {
  returnBillingPricingStatus?: ReturnBillingRollout | null;
};

type ReturnBillingSummaryValues = {
  carrierPostage: number;
  labelFees: number;
  keepItemFees: number;
  totalBilled: number;
  billedEventCount: number;
  shadowAmount: number;
  shadowCarrierPostage: number;
  shadowLabelFees: number;
  shadowKeepItemFees: number;
  shadowEventCount: number;
  pendingEventCount: number;
  failedEventCount: number;
  currencyCode: string | null;
  hasInvalidCurrency: boolean;
};

const nonNegativeNumber = (value: unknown) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
};

const aggregateCount = (bucket?: ReturnBillingAggregateBucket | null) =>
  nonNegativeNumber(bucket?.aggregate?.count);

const aggregateSum = (
  bucket: ReturnBillingAggregateBucket | null | undefined,
  field: keyof ReturnBillingAggregateSum,
) => nonNegativeNumber(bucket?.aggregate?.sum?.[field]);

export const isReturnBillingV2Enabled = (
  rollout?: ReturnBillingRollout | null,
) =>
  Number(rollout?.pricingVersion) === 2 &&
  (rollout?.rolloutMode === 'SHADOW' || rollout?.rolloutMode === 'LIVE');

export const summarizeReturnBillingData = (
  data?: ReturnBillingSummaryQueryResult | null,
): ReturnBillingSummaryValues => {
  const liveLabelCount = aggregateCount(data?.liveLabels);
  const liveKeepItemCount = aggregateCount(data?.liveKeepItems);
  const shadowLabelCount = aggregateCount(data?.shadowLabels);
  const shadowKeepItemCount = aggregateCount(data?.shadowKeepItems);

  return {
    carrierPostage: aggregateSum(data?.liveLabels, 'carrierCost'),
    labelFees: aggregateSum(data?.liveLabels, 'serviceFee'),
    keepItemFees: aggregateSum(data?.liveKeepItems, 'serviceFee'),
    totalBilled:
      aggregateSum(data?.liveLabels, 'amount') +
      aggregateSum(data?.liveKeepItems, 'amount'),
    billedEventCount: liveLabelCount + liveKeepItemCount,
    shadowAmount:
      aggregateSum(data?.shadowLabels, 'amount') +
      aggregateSum(data?.shadowKeepItems, 'amount'),
    shadowCarrierPostage: aggregateSum(data?.shadowLabels, 'carrierCost'),
    shadowLabelFees: aggregateSum(data?.shadowLabels, 'serviceFee'),
    shadowKeepItemFees: aggregateSum(data?.shadowKeepItems, 'serviceFee'),
    shadowEventCount: shadowLabelCount + shadowKeepItemCount,
    pendingEventCount: aggregateCount(data?.pending),
    failedEventCount: aggregateCount(data?.failed),
    currencyCode: 'USD',
    hasInvalidCurrency: aggregateCount(data?.invalidCurrency) > 0,
  };
};

const formatBillingMoney = (value: number, currencyCode: string) => {
  try {
    return `${new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyCode,
    }).format(value)} ${currencyCode}`;
  } catch {
    return `${currencyCode} ${value.toFixed(2)}`;
  }
};

type ReturnBillingSummaryProps = {
  data?: ReturnBillingSummaryQueryResult | null;
  loading: boolean;
  error?: Error | null;
  rollout?: ReturnBillingRollout | null;
  rolloutLoading?: boolean;
  rolloutError?: Error | null;
};

const LegacyReturnBilling = () => (
  <div className="px-4 py-5">
    <p className="text-sm font-semibold text-gray-900">Legacy return billing</p>
    <p className="mt-1 text-sm leading-relaxed text-gray-600">
      This store remains on processed-return pricing: 0–500 monthly returns:{' '}
      $1.00 each; 501–2,000: $0.75 each; 2,001–5,000: $0.50 each; 5,000+: custom
      pricing.
    </p>
    <p className="mt-2 text-xs text-gray-500">
      Outcome-based label and Keep Your Item billing is not active for this
      store.
    </p>
  </div>
);

export default function ReturnBillingSummary({
  data,
  loading,
  error,
  rollout,
  rolloutLoading = false,
  rolloutError = null,
}: ReturnBillingSummaryProps) {
  const summary = summarizeReturnBillingData(data);
  const v2Enabled = isReturnBillingV2Enabled(rollout);
  const isShadowMode = rollout?.rolloutMode === 'SHADOW';
  const hasLiveRelevantEvents =
    summary.billedEventCount +
      summary.pendingEventCount +
      summary.failedEventCount >
    0;
  const currencyCode = summary.currencyCode || 'USD';
  const formatMoney = (value: number) =>
    formatBillingMoney(value, currencyCode);
  const shadowCards = [
    {
      label: 'Projected carrier postage',
      amount: summary.shadowCarrierPostage,
      description: 'Postage purchased through ShipAid rates',
    },
    {
      label: 'Projected ShipAid label fees',
      amount: summary.shadowLabelFees,
      description: '$1.75 per successfully created label',
    },
    {
      label: 'Projected Keep Your Item fees',
      amount: summary.shadowKeepItemFees,
      description: '$0.75 per completed resolution',
    },
    {
      label: 'Projected total',
      amount: summary.shadowAmount,
      description: `${summary.shadowEventCount} shadow event${
        summary.shadowEventCount === 1 ? '' : 's'
      }`,
    },
  ];
  const liveCards = [
    {
      label: 'Carrier postage',
      amount: summary.carrierPostage,
      description: 'Postage purchased through ShipAid rates',
    },
    {
      label: 'ShipAid label fees',
      amount: summary.labelFees,
      description: '$1.75 per successfully created label',
    },
    {
      label: 'Keep Your Item fees',
      amount: summary.keepItemFees,
      description: '$0.75 per completed resolution',
    },
    {
      label: 'Total billed',
      amount: summary.totalBilled,
      description: `${summary.billedEventCount} completed live event${
        summary.billedEventCount === 1 ? '' : 's'
      }`,
    },
  ];
  const historicalCards = [
    { ...liveCards[0], label: 'Historical carrier postage' },
    { ...liveCards[1], label: 'Historical ShipAid label fees' },
    { ...liveCards[2], label: 'Historical Keep Your Item fees' },
    { ...liveCards[3], label: 'Historical total billed' },
  ];
  const renderCards = (
    values: Array<{ label: string; amount: number; description: string }>,
  ) => (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
      {values.map((card) => (
        <div
          key={card.label}
          className="rounded-md border border-gray-200 bg-gray-50 p-3"
        >
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
            {card.label}
          </p>
          <p className="mt-1 text-xl font-bold text-gray-900">
            {formatMoney(card.amount)}
          </p>
          <p className="mt-1 text-xs leading-relaxed text-gray-500">
            {card.description}
          </p>
        </div>
      ))}
    </div>
  );
  const historicalDetails = loading ? (
    <p
      role="status"
      className="border-t border-gray-100 px-4 py-4 text-sm text-gray-500"
    >
      Loading historical return billing…
    </p>
  ) : error ? (
    <p
      role="alert"
      className="border-t border-gray-100 px-4 py-4 text-sm font-medium text-red-700"
    >
      Historical return billing couldn&apos;t be loaded. Try refreshing.
    </p>
  ) : summary.hasInvalidCurrency ? (
    <p
      role="alert"
      className="border-t border-gray-100 px-4 py-4 text-sm font-medium text-red-700"
    >
      Historical billing totals are unavailable because the ledger contains
      mixed, missing, or unsupported currencies.
    </p>
  ) : summary.billedEventCount > 0 ? (
    <div className="border-t border-gray-100 p-4">
      <h4 className="text-sm font-semibold text-gray-900">
        Historical pricing v2 charges
      </h4>
      <p className="mb-3 mt-1 text-xs text-gray-500">
        {rolloutError
          ? 'Completed live charges in this UTC range remain visible even while the current rollout status is unavailable.'
          : isShadowMode
          ? 'Completed live charges in this UTC range remain visible while outcome billing is in shadow mode.'
          : 'Completed live charges in this UTC range remain visible after outcome billing was turned off.'}
      </p>
      {renderCards(historicalCards)}
    </div>
  ) : null;

  return (
    <section className="mt-4 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
      <div className="border-b border-gray-100 px-4 py-3">
        <h3 className="text-sm font-semibold text-gray-900">
          {isShadowMode
            ? 'Return & exchange billing preview'
            : 'Return & exchange billing'}
        </h3>
        <p className="mt-1 text-xs text-gray-500">
          {isShadowMode
            ? 'Pricing v2 shadow estimates. No charges are issued in shadow mode.'
            : rolloutError
            ? 'Current billing mode is unavailable. Completed historical pricing v2 charges remain visible.'
            : !v2Enabled
            ? 'Current store pricing plus completed historical pricing v2 charges.'
            : 'Only completed live pricing v2 charges are included in billed totals.'}{' '}
          Date boundaries use UTC. Currency is shown explicitly.
        </p>
        <p className="mt-1 text-xs text-gray-500">
          Keep Your Item fees cover the legacy keep-item resolution, not
          keep-original exchange incentives.
        </p>
      </div>

      {rolloutLoading ? (
        <p role="status" className="px-4 py-6 text-sm text-gray-500">
          Loading return billing configuration…
        </p>
      ) : rolloutError ? (
        <div>
          <p
            role="alert"
            className="px-4 py-4 text-sm font-medium text-amber-800"
          >
            Return billing configuration couldn&apos;t be loaded. Current
            pricing mode is not shown; try refreshing.
          </p>
          {historicalDetails}
        </div>
      ) : !v2Enabled ? (
        <div>
          <LegacyReturnBilling />
          {historicalDetails}
        </div>
      ) : loading ? (
        <p role="status" className="px-4 py-6 text-sm text-gray-500">
          Loading return billing…
        </p>
      ) : error ? (
        <p role="alert" className="px-4 py-6 text-sm font-medium text-red-700">
          Return billing data couldn&apos;t be loaded. Try refreshing.
        </p>
      ) : summary.hasInvalidCurrency ? (
        <p role="alert" className="px-4 py-6 text-sm font-medium text-red-700">
          Billing totals are unavailable because the ledger contains mixed,
          missing, or unsupported currencies. Pricing v2 currently supports USD
          only.
        </p>
      ) : isShadowMode ? (
        <div>
          <div className="p-4">
            {summary.shadowEventCount > 0 ? (
              renderCards(shadowCards)
            ) : (
              <p className="text-sm text-gray-500">
                No shadow estimates in this UTC date range.
              </p>
            )}
          </div>
          {summary.billedEventCount > 0 && historicalDetails}
        </div>
      ) : !hasLiveRelevantEvents ? (
        <p className="px-4 py-6 text-sm text-gray-500">
          No pricing v2 billing events in this UTC date range.
        </p>
      ) : (
        <div className="p-4">
          {renderCards(liveCards)}

          {!isShadowMode &&
            (summary.pendingEventCount > 0 || summary.failedEventCount > 0) && (
              <p className="mt-3 text-xs text-gray-500">
                Not included in total billed: {summary.pendingEventCount}{' '}
                pending, {summary.failedEventCount} failed.
              </p>
            )}
        </div>
      )}
    </section>
  );
}
